import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import Logger from "../../services/Logger";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import validator from "validator";
import ReferralService from "../../services/ReferralService";
import CustomSnackbar from "../components/CustomSnackbar";
import CardCongratulations from "./CardCongratulations";
import CardEventInformation from "./CardEventInformation";
import CardInvitationHistory from "./CardInvitationHistory";
import CardSendInvitations from "./CardSendInvitations";
import { trackEvent } from "../../services/GoogleAnalytics";

export default function ReferralEventPage() {
    const { eventId } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [emailInputs, setEmailInputs] = useState([""]);
    const [sending, setSending] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [invites, setInvites] = useState([]);
    const [loadingInvites, setLoadingInvites] = useState(false);

    useEffect(() => {
        trackEvent("entered", { name: "ReferralEventPage" });
    }, []);

    const updateTimeRemaining = useCallback(() => {
        if (!event || !event.endAt) return;

        const now = new Date().getTime();
        const endTime = event.endAt;
        const diff = endTime - now;

        if (diff <= 0) {
            setTimeRemaining({ isOver: true });
            return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        setTimeRemaining({ days, hours, minutes, seconds, isOver: false });
    }, [event]);

    useEffect(() => {
        if (eventId) {
            loadEvent();
        } else {
            setError("Event ID is required");
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        if (event && event.endAt) {
            const interval = setInterval(() => {
                updateTimeRemaining();
            }, 1000);

            updateTimeRemaining();

            return () => clearInterval(interval);
        }
    }, [event, updateTimeRemaining]);

    const loadEvent = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await ReferralService.getEvent(eventId);
            if (result) {
                setEvent(result);
            } else {
                setError("Referral event not found");
            }
            setLoading(false);
        } catch (err) {
            Logger.error("Error fetching event:", err);
            setError(err.message || "Failed to load event");
            setLoading(false);
        }
    };

    const loadInvites = useCallback(async () => {
        if (!eventId) return;

        try {
            setLoadingInvites(true);
            const UserService = (await import("../../services/UserService")).default;
            const userInfo = await UserService.getUserInfo();
            const cloudUserId = userInfo?.cloudUser?.id;

            if (!cloudUserId) {
                setLoadingInvites(false);
                return;
            }

            const result = await ReferralService.getInvites(eventId, cloudUserId);
            if (Array.isArray(result)) {
                setInvites(result);
            } else {
                setInvites([]);
            }
        } catch (err) {
            Logger.error("Error fetching invites:", err);
            setInvites([]);
        } finally {
            setLoadingInvites(false);
        }
    }, [eventId]);

    useEffect(() => {
        if (event && eventId) {
            loadInvites();
        }
    }, [event, eventId, loadInvites]);

    const formatTimeRemaining = () => {
        if (!timeRemaining) return "Calculating...";

        if (timeRemaining.isOver) {
            return "Event Ended";
        }

        const { days, hours, minutes, seconds } = timeRemaining;

        if (days > 0) {
            return `${days} day${days !== 1 ? "s" : ""} left`;
        } else if (hours > 0) {
            return `${hours} hour${hours !== 1 ? "s" : ""} left`;
        } else if (minutes > 0) {
            return `${minutes} minute${minutes !== 1 ? "s" : ""} left`;
        } else {
            return `${seconds} second${seconds !== 1 ? "s" : ""} left`;
        }
    };

    const getTimeRemainingColor = () => {
        if (!timeRemaining || timeRemaining.isOver) {
            return "error";
        }

        const { days, hours } = timeRemaining;

        if (days === 0 && hours < 24) {
            return "error"; // Less than 24 hours - red
        } else if (days <= 3) {
            return "warning"; // 1-3 days - orange
        } else {
            return "success"; // More than 3 days - green
        }
    };

    const getClockColor = () => {
        if (!timeRemaining || timeRemaining.isOver) {
            return "#f44336";
        }

        const { days, hours } = timeRemaining;

        if (days === 0 && hours < 24) {
            return "#ff9800"; // Less than 24 hours - orange
        } else if (days <= 3) {
            return "#ffc107"; // 1-3 days - yellow
        } else {
            return "#4caf50"; // More than 3 days - green
        }
    };

    const handleEmailChange = (index, value) => {
        const newInputs = [...emailInputs];
        newInputs[index] = value;
        setEmailInputs(newInputs);
    };



    const handleSendInvitations = async () => {
        const validEmails = emailInputs.filter((email) => email.trim() !== "");
        if (validEmails.length === 0) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Please enter at least one email address");
            setOpenSnackbar(true);
            return;
        }

        // Validate email format using validator
        const invalidEmails = validEmails.filter((email) => !validator.isEmail(email.trim()));
        if (invalidEmails.length > 0) {
            setSnackbarSeverity("error");
            setSnackbarMessage(`Invalid email format: ${invalidEmails.join(", ")}`);
            setOpenSnackbar(true);
            return;
        }

        if (!event || !event.id) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Event information is missing");
            setOpenSnackbar(true);
            return;
        }

        // Check invitation limit: requiredCount * 2
        const maxInvites = (event.requiredCount || 5) * 2;
        const currentInvitesCount = invites.length;
        const newInvitesCount = validEmails.length;
        const totalAfterSending = currentInvitesCount + newInvitesCount;

        if (totalAfterSending > maxInvites) {
            const remaining = Math.max(0, maxInvites - currentInvitesCount);
            setSnackbarSeverity("error");
            setSnackbarMessage(
                `Invitation limit exceeded. You can send ${remaining} more invitation${remaining !== 1 ? "s" : ""} (Maximum: ${maxInvites} invitations)`
            );
            setOpenSnackbar(true);
            return;
        }

        setSending(true);
        try {
            // Get current user ID
            const UserService = (await import("../../services/UserService")).default;
            const userInfo = await UserService.getUserInfo();
            const cloudUserId = userInfo?.cloudUser?.id;

            if (!cloudUserId) {
                throw new Error("User not authenticated");
            }

            // Send invitations for each email
            const results = await Promise.all(
                validEmails.map((email) =>
                    ReferralService.sendInvitation(cloudUserId, event.id, email.trim())
                )
            );

            setSnackbarSeverity("success");
            setSnackbarMessage(`Successfully sent ${results.length} invitation(s)`);
            setOpenSnackbar(true);
            setEmailInputs([""]);
            
            // Reload invites after sending to refresh the invitation history
            await loadInvites();
        } catch (err) {
            Logger.error("Error sending invitations:", err);
            setSnackbarSeverity("error");
            setSnackbarMessage(err.message || "Failed to send invitations");
            setOpenSnackbar(true);
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3, maxWidth: 1200, mx: "auto" }}>
            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {!loading && !error && event && (() => {
                // Count how many people have joined (check joinedAt is not empty)
                const joinedCount = invites.filter(
                    (invite) => invite.joinedAt && invite.joinedAt !== null && invite.joinedAt !== ""
                ).length;
                const requiredCount = event.requiredCount || 5;
                const hasReachedGoal = joinedCount >= requiredCount;

                return (
                    <Grid container spacing={3}>
                        {/* Event Information */}
                        <CardEventInformation
                            event={event}
                            timeRemaining={timeRemaining}
                            formatTimeRemaining={formatTimeRemaining}
                            getTimeRemainingColor={getTimeRemainingColor}
                            getClockColor={getClockColor}
                        />

                        {hasReachedGoal ? (
                            /* Congratulations */
                            <CardCongratulations event={event} joinedCount={joinedCount} />
                        ) : (
                            <>
                                {/* Email Input Section */}
                                <CardSendInvitations
                                    event={event}
                                    invites={invites}
                                    emailInputs={emailInputs}
                                    sending={sending}
                                    timeRemaining={timeRemaining}
                                    onEmailChange={handleEmailChange}
                                    onSendInvitations={handleSendInvitations}
                                />

                                {loadingInvites && (
                                    <Grid size={{ xs: 12 }}>
                                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    </Grid>
                                )}

                                {/* Invites List */}
                                <CardInvitationHistory event={event} invites={invites} hasReachedGoal={hasReachedGoal} />
                            </>
                        )}
                    </Grid>
                );
            })()}

            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                severity={snackbarSeverity}
                message={snackbarMessage}
            />
        </Box>
    );
}

